import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';
import BookingStats from './EventBookingsComponents/BookingStats';
import BookingFilters from './EventBookingsComponents/BookingFilters';
import BookingTable from './EventBookingsComponents/BookingTable';
import ConfirmDialog from './EventBookingsComponents/ConfirmDialog';

const EventBookings = () => {
  const [bookings, setBookings] = useState({ active: [], inactive: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [activeTab, setActiveTab] = useState('active');
  const [businessId, setBusinessId] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('All');

  useEffect(() => {
    const token = localStorage.getItem('token_partner_shop');
    const id = localStorage.getItem('id_partner_shop');

    if (token && id) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setBusinessId(payload.business_id || payload._id || id);
      } catch (err) {
        setError('Authentication required. Please login again.');
        setLoading(false);
      }
    } else {
      setError('Authentication required. Please login again.');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (businessId) {
      fetchBookings();

      const interval = setInterval(() => {
        fetchBookings(true);
      }, 15000);

      return () => clearInterval(interval);
    }
  }, [businessId]);

  const fetchBookings = async (isSilent = false) => {
    try {
      if (!isSilent) setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/reservation/business/${businessId}/Shop`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token_partner_shop')}`
          }
        }
      );

      if (response.data) {
        let allBookings = [];
        if (Array.isArray(response.data.data)) {
          allBookings = response.data.data;
        } else if (Array.isArray(response.data.active) || Array.isArray(response.data.inactive)) {
          allBookings = [...(response.data.active || []), ...(response.data.inactive || [])];
        } else if (Array.isArray(response.data)) {
          allBookings = response.data;
        }

        const activeBookings = allBookings.filter(booking =>
          booking.status === 'Pending' || booking.status === 'Confirmed'
        );
        const inactiveBookings = allBookings.filter(booking =>
          booking.status === 'Cancelled' || booking.status === 'Completed'
        );
        setBookings({ active: activeBookings, inactive: inactiveBookings });
      } else {
        setBookings({ active: [], inactive: [] });
      }
    } catch (error) {
      setError('Error fetching bookings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/reservation/${bookingId}`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token_partner_shop')}`
          }
        }
      );

      if (response.status === 200) {
        toast.success(`Booking ${newStatus.toLowerCase()} successfully`);
        fetchBookings();
      }
    } catch (error) {
      toast.error('Error updating booking status. Please try again.');
    }
  };

  const handleStatusChange = (bookingId, newStatus) => {
    if (newStatus === 'Cancelled') {
      setSelectedBookingId(bookingId);
      setShowConfirmDialog(true);
    } else {
      updateBookingStatus(bookingId, newStatus);
    }
  };

  const formatDateTime = (dateString, timeString) => {
    try {
      const date = new Date(dateString);
      return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} at ${timeString}`;
    } catch (error) {
      return `${dateString} at ${timeString}`;
    }
  };

  const filteredBookings = bookings[activeTab].filter(booking => {
    const customer = booking.booking_id || booking.user_id || {};
    const matchesSearch =
      customer?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer?.phone_number?.includes(searchQuery) ||
      customer?.email?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesDate = !selectedDate || booking.date.includes(selectedDate);
    const matchesStatus = statusFilter === 'All' || booking.status === statusFilter;

    return matchesSearch && matchesDate && matchesStatus;
  });

  const stats = {
    total: bookings.active.length + bookings.inactive.length,
    pending: bookings.active.filter(b => b.status === 'Pending').length,
    active: bookings.active.filter(b => b.status === 'Confirmed').length,
    completed: bookings.inactive.filter(b => b.status === 'Completed').length
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-96 space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        <p className="text-gray-500 font-medium animate-pulse">Loading orders...</p>
      </div>
    );
  }

  if (error) {
    return <div className="p-8 text-center text-rose-500 font-bold">{error}</div>;
  }

  return (
    <div className="p-4 sm:p-6 space-y-8 animate-in fade-in duration-700">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-primary tracking-tight">Order Management</h1>
          <p className="text-gray-500 font-medium mt-1">Manage and track your customer orders and visits.</p>
        </div>
        <button
          onClick={fetchBookings}
          className="px-6 py-3 bg-white/60 backdrop-blur-md border border-gray-100 rounded-2xl text-gray-600 font-black text-[10px] shadow-sm hover:shadow-md transition-all flex items-center gap-2 uppercase tracking-widest"
        >
          <Clock size={16} /> Refresh Orders
        </button>
      </div>

      <BookingStats stats={stats} />

      <BookingFilters
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        bookingsCount={{ active: bookings.active.length, inactive: bookings.inactive.length }}
      />

      <BookingTable
        filteredBookings={filteredBookings}
        activeTab={activeTab}
        onStatusChange={handleStatusChange}
        formatDateTime={formatDateTime}
      />

      <ConfirmDialog
        isOpen={showConfirmDialog}
        onCancel={() => setShowConfirmDialog(false)}
        onConfirm={() => {
          updateBookingStatus(selectedBookingId, 'Cancelled');
          setShowConfirmDialog(false);
        }}
      />
    </div>
  );
};

export default EventBookings;
